'use client';

import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import {
  ArrowRight,
  Clapperboard,
  Coins,
  Eye,
  HelpCircle,
  Lightbulb,
  Play,
  Sparkles,
  Video,
  Wand2,
  Zap,
} from 'lucide-react';
import { Suspense } from 'react';

export default function HowItWorksPage() {
  return (
    <div className="flex min-h-screen flex-col bg-white font-sans">
      <Suspense
        fallback={
          <div className="fixed top-0 right-0 left-0 z-50 h-[72px] border-zinc-100 border-b bg-white shadow-sm" />
        }
      >
        <Header />
      </Suspense>
      <main id="main-content" className="dark flex-1 bg-[#212120] pt-[100px] pb-12 text-white">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h1 className="mb-6 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-extrabold text-5xl text-transparent tracking-tight sm:text-6xl md:text-7xl">
              How It Works
            </h1>
            <p className="mx-auto max-w-2xl px-4 text-lg text-zinc-400 sm:text-xl">
              Create stunning, professional-quality videos in minutes using the power of AI. No
              video editing skills required.
            </p>
          </div>

          <div className="mx-auto max-w-4xl space-y-16">
            {/* What is AI Video Generation */}
            <section className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-8 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Video className="h-6 w-6 text-primary" />
                </div>
                <h2 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                  What is AI Video Generation?
                </h2>
              </div>
              <div className="space-y-4 text-zinc-400">
                <p>
                  <strong>AI video generation</strong> is a revolutionary technology that creates
                  videos from simple text descriptions. Instead of spending hours filming, editing,
                  and adding effects, you simply describe what you want to see — and the AI creates
                  it for you.
                </p>
                <p>
                  Imagine typing &quot;a golden retriever running through a field of sunflowers at
                  sunset&quot; and receiving a beautiful, cinematic video moments later. That&apos;s
                  the power of AI video generation.
                </p>
                <p>
                  This technology is transforming how businesses create marketing content, how
                  educators build learning materials, and how creators bring their visions to life —
                  all without expensive equipment or technical expertise.
                </p>
              </div>
            </section>

            {/* What is Sora 2 */}
            <section className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-8 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h2 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                  What is Sora 2?
                </h2>
              </div>
              <div className="space-y-4 text-zinc-400">
                <p>
                  <strong>Sora 2</strong> is OpenAI&apos;s latest and most advanced AI video
                  generation model. It represents a massive leap forward in video quality, realism,
                  and consistency.
                </p>
                <p>
                  Unlike earlier AI video tools that produced short, often unrealistic clips, Sora 2
                  can generate:
                </p>
                <ul className="ml-4 list-inside list-disc space-y-2">
                  <li>
                    <strong>Longer videos</strong> — up to 20 seconds of smooth, coherent footage
                  </li>
                  <li>
                    <strong>Realistic motion</strong> — natural movement, physics, and lighting
                  </li>
                  <li>
                    <strong>Complex scenes</strong> — multiple subjects, detailed backgrounds, and
                    dynamic camera angles
                  </li>
                  <li>
                    <strong>Consistent characters</strong> — people and objects that look the same
                    throughout the video
                  </li>
                  <li>
                    <strong>High resolution</strong> — up to 1080p HD quality
                  </li>
                </ul>
                <p>
                  ZappingAI provides easy access to Sora 2 through our platform, so you can harness
                  this cutting-edge technology without needing technical expertise.
                </p>
              </div>
            </section>

            {/* 3 Simple Steps */}
            <section>
              <h2 className="mb-8 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text text-center font-bold text-2xl text-transparent">
                Get Started in 3 Simple Steps
              </h2>

              <div className="space-y-8">
                {/* Step 1 */}
                <div className="flex flex-col gap-6 rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 shadow-sm md:flex-row md:items-start">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-2xl text-white">
                    1
                  </div>
                  <div>
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-transparent text-xl">
                      Create Your Free Account
                    </h3>
                    <p className="mb-3 text-zinc-400">
                      Sign up in seconds using Google, Microsoft, LinkedIn, or email. No credit card
                      required to register.
                    </p>
                    <p className="text-zinc-400">
                      <strong>Ready?</strong> To generate videos, choose a{' '}
                      <Link href="/pricing" className="text-blue-400 underline">
                        plan
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex flex-col gap-6 rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 shadow-sm md:flex-row md:items-start">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-2xl text-white">
                    2
                  </div>
                  <div>
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-transparent text-xl">
                      Describe Your Video
                    </h3>
                    <p className="mb-3 text-zinc-400">
                      Use our simple text interface to describe the video you want. Tell the AI
                      about the scene, style, camera movement, and mood. Tip: Upload an image to use
                      as start frame - then it just needs to be animated.
                    </p>
                    <p className="text-zinc-400">
                      <strong>Example prompt:</strong> &quot;A majestic eagle soaring over
                      snow-capped mountains at sunrise, cinematic drone footage, slow motion&quot;
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex flex-col gap-6 rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 shadow-sm md:flex-row md:items-start">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-2xl text-white">
                    3
                  </div>
                  <div>
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-transparent text-xl">
                      Generate & Download
                    </h3>
                    <p className="mb-3 text-zinc-400">
                      Click generate and wait 2-5 minutes while Sora 2 creates your video. Preview
                      it instantly, see it in your gallery, download as MP4.
                    </p>
                    <p className="text-zinc-400">
                      <strong>Want more?</strong> Refine your prompt and regenerate the video. The
                      AI learns from specific details, so be very specific.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Prompt Tips */}
            <section className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-8 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
                  <Lightbulb className="h-6 w-6 text-green-500" />
                </div>
                <h2 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                  Tips for Better Results
                </h2>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-3">
                  <h3 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-lg text-transparent">
                    ✓ Do This
                  </h3>
                  <ul className="space-y-2 text-zinc-400">
                    <li>
                      • Be specific: &quot;golden hour lighting&quot; vs &quot;nice lighting&quot;
                    </li>
                    <li>
                      • Describe camera movement: &quot;slow pan&quot;, &quot;tracking shot&quot;
                    </li>
                    <li>
                      • Set the mood: &quot;peaceful&quot;, &quot;dramatic&quot;,
                      &quot;energetic&quot;
                    </li>
                    <li>
                      • Mention style: &quot;cinematic&quot;, &quot;documentary&quot;,
                      &quot;anime&quot;
                    </li>
                    <li>• Include details about colors and textures</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-lg text-transparent">
                    ✗ Avoid This
                  </h3>
                  <ul className="space-y-2 text-zinc-400">
                    <li>• Vague descriptions: &quot;make a cool video&quot;</li>
                    <li>• Too many subjects in one scene</li>
                    <li>• Requesting specific copyrighted content</li>
                    <li>• Overly complex multi-scene narratives</li>
                    <li>• Requests for realistic human faces (still improving)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Where to Find Examples */}
            <section className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-8 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10">
                  <Eye className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                  See Examples & Get Inspired
                </h2>
              </div>
              <div className="space-y-4 text-zinc-400">
                <p>
                  Before creating your own videos, explore what&apos;s possible by viewing examples:
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-4">
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent text-xl">
                      🎬 Explore our Examples
                    </h3>
                    <p className="text-sm">
                      Visit our{' '}
                      <Link
                        href="/sora-2-ai-video-generator#explore"
                        className="text-blue-400 underline"
                      >
                        explore
                      </Link>{' '}
                      page to see featured videos created with Sora 2.
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-4">
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent text-xl">
                      📱 Social Media
                    </h3>
                    <p className="text-sm">
                      Follow #ZappingAI on Twitter/X, TikTok, and YouTube to see what others are
                      creating.
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-4">
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent text-xl">
                      🖼️ Your Gallery
                    </h3>
                    <p className="text-sm">
                      After signing up, all your created videos are saved to your own personal
                      gallery for reference.
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-4">
                    <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent text-xl">
                      📖 25% More Than OpenAI
                    </h3>
                    <p className="text-sm">Get 25% more video generation options than OpenAI. </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Use Cases */}
            <section>
              <h2 className="mb-6 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text text-center font-bold text-2xl text-transparent">
                What Can You Create?
              </h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 text-center shadow-sm">
                  <div className="mb-3 text-3xl" role="img" aria-label="Megaphone">
                    📢
                  </div>
                  <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent">
                    Marketing Content
                  </h3>
                  <p className="text-sm text-zinc-400">Product demos, social ads, promo videos</p>
                </div>
                <div className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 text-center shadow-sm">
                  <div className="mb-3 text-3xl" role="img" aria-label="Graduation cap">
                    🎓
                  </div>
                  <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent">
                    Educational Videos
                  </h3>
                  <p className="text-sm text-zinc-400">Explainers, tutorials, course content</p>
                </div>
                <div className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 text-center shadow-sm">
                  <div className="mb-3 text-3xl" role="img" aria-label="Artist palette">
                    🎨
                  </div>
                  <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent">
                    Creative Projects
                  </h3>
                  <p className="text-sm text-zinc-400">Art, music videos, short films</p>
                </div>
                <div className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 text-center shadow-sm">
                  <div className="mb-3 text-3xl" role="img" aria-label="Mobile phone">
                    📱
                  </div>
                  <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent">
                    Social Content
                  </h3>
                  <p className="text-sm text-zinc-400">TikToks, Reels, YouTube Shorts</p>
                </div>
                <div className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 text-center shadow-sm">
                  <div className="mb-3 text-3xl" role="img" aria-label="Briefcase">
                    💼
                  </div>
                  <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent">
                    Business Presentations
                  </h3>
                  <p className="text-sm text-zinc-400">Pitch decks, company videos, training</p>
                </div>
                <div className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-6 text-center shadow-sm">
                  <div className="mb-3 text-3xl" role="img" aria-label="Game controller">
                    🎮
                  </div>
                  <h3 className="mb-2 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-semibold text-transparent">
                    Game & App Previews
                  </h3>
                  <p className="text-sm text-zinc-400">Trailers, teasers, concept visualizations</p>
                </div>
              </div>
            </section>

            {/* Credits Info */}
            <section className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] p-8 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Coins className="h-6 w-6 text-primary" />
                </div>
                <h2 className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                  Understanding Credits
                </h2>
              </div>
              <div className="space-y-4 text-zinc-400">
                <p>
                  We use a simple credit-based system. Each video generation uses credits based on:
                </p>
                <ul className="ml-4 list-inside list-disc space-y-1">
                  <li>
                    <strong>Video length</strong> — longer videos use more credits
                  </li>
                  <li>
                    <strong>Resolution</strong> — HD uses more than standard definition
                  </li>
                  <li>
                    <strong>Model tier</strong> — Sora 2 Pro uses more than standard
                  </li>
                </ul>
                <p className="font-semibold text-blue-400">
                  Check our{' '}
                  <Link href="/pricing" className="underline hover:text-white">
                    Plans & Pricing
                  </Link>{' '}
                  for details on credit monthly or annual subscription plans.
                </p>
              </div>
            </section>

            {/* CTA */}
            <section className="py-8 text-center">
              <h2 className="mb-4 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                Ready to Create Your First Video?
              </h2>
              <p className="mb-6 text-zinc-400">
                Sign up free and start generating videos with Sora 2 today.
              </p>
              <Link
                href="/sora-2-ai-video-generator"
                className="btn-purple inline-flex h-12 items-center gap-2 rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] px-8 font-bold text-white shadow-md transition-all hover:scale-105"
              >
                Get Started <ArrowRight className="h-4 w-4" />
              </Link>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
